import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUnreadCounts, setCount, resetCount } from '../Redux/Slices/MessageCountSlice';
import { URL } from '../components/BaseUrl';
import { decryptToken } from '../Utlis/Encryption';
import MessagingSocket from '../services/messagingSocket';

const useGlobalUnreadCount = () => {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const intervalRef = useRef(null);
  const refetchTimeoutRef = useRef(null);
  const { totalCount, threads, roleBased } = useSelector((state) => state.messageCount);

  // Get token
  const rawEncryptedToken = localStorage.getItem('token');
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  // Fetch unread counts from API
  const fetchUnreadCounts = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${URL}/api/messages/unread-counts/detailed`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('📬 Failed to fetch unread counts:', response.status);
        return;
      }

      const data = await response.json();
      
      if (!data.error && data.data) {
        // API Response: { totalUnread: 5, threadCounts: [{ threadId, unreadCount, role }] }
        const { totalUnread, threadCounts } = data.data;
        
        // Convert threadCounts array to object
        let threadsObj = {};
        let roleBasedCounts = {
          account_executive: 0,
          company_admin: 0,
          worker: 0,
          super_admin: 0,
        };

        if (Array.isArray(threadCounts)) {
          threadCounts.forEach(t => {
            if (t.threadId) {
              threadsObj[t.threadId] = t.unreadCount || 0;
            }
            // Aggregate by role
            if (t.role && roleBasedCounts.hasOwnProperty(t.role)) {
              roleBasedCounts[t.role] += (t.unreadCount || 0);
            }
          });
        }

        dispatch(setUnreadCounts({
          total: totalUnread || 0,
          threads: threadsObj,
          roleBased: roleBasedCounts,
        }));

        console.log('📬 Unread counts fetched:', totalUnread);
      }
    } catch (error) {
      console.error('📬 Error fetching unread counts:', error);
    }
  }, [token, dispatch]);

  // Delayed refetch to allow server to process
  const delayedRefetch = useCallback(() => {
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
    }
    refetchTimeoutRef.current = setTimeout(() => {
      fetchUnreadCounts();
    }, 500);
  }, [fetchUnreadCounts]);

  // Initialize socket connection
  const initSocket = useCallback(() => {
    if (!token || socketRef.current) return;

    const socket = new MessagingSocket(token);
    socketRef.current = socket;
    socket.connect();

    // Listen for total unread count updates - Direct update from server
    socket.on('total_unread_count_updated', (data) => {
      console.log('📬 Socket: total_unread_count_updated', data);
      if (data) {
        // Handle different response formats
        if (typeof data.totalUnread === 'number') {
          dispatch(setCount(data.totalUnread));
          
          // Also update thread counts if provided
          if (Array.isArray(data.threadCounts)) {
            let threadsObj = {};
            let roleBasedCounts = {
              account_executive: 0,
              company_admin: 0,
              worker: 0,
              super_admin: 0,
            };
            
            data.threadCounts.forEach(t => {
              if (t.threadId) {
                threadsObj[t.threadId] = t.unreadCount || 0;
              }
              if (t.role && roleBasedCounts.hasOwnProperty(t.role)) {
                roleBasedCounts[t.role] += (t.unreadCount || 0);
              }
            });
            
            dispatch(setUnreadCounts({
              total: data.totalUnread,
              threads: threadsObj,
              roleBased: roleBasedCounts,
            }));
          }
        } else if (typeof data.total_unread_count === 'number') {
          dispatch(setCount(data.total_unread_count));
        } else if (typeof data.count === 'number') {
          dispatch(setCount(data.count));
        }
      }
    });

    // Listen for thread-specific unread count updates - Triggers refetch
    socket.on('unread_count_updated', (data) => {
      console.log('📬 Socket: unread_count_updated', data);
      delayedRefetch();
    });

    // Listen for new messages - Triggers refetch after delay
    socket.on('new_message', (data) => {
      console.log('📬 Socket: new_message', data);
      delayedRefetch();
    });

    // Listen for messages read - Triggers refetch after delay
    socket.on('messages_read', (data) => {
      console.log('📬 Socket: messages_read', data);
      delayedRefetch();
    });

    return socket;
  }, [token, dispatch, delayedRefetch]);

  // Handle visibility change (refetch when tab becomes visible)
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && token) {
      console.log('📬 Tab visible, refreshing unread counts');
      fetchUnreadCounts();
    }
  }, [fetchUnreadCounts, token]);

  // Main effect - initialize on mount
  useEffect(() => {
    if (!token) {
      // Reset counts if no token (logged out)
      dispatch(resetCount());
      return;
    }

    // Initial fetch
    fetchUnreadCounts();

    // Initialize socket
    const socket = initSocket();

    // Set up periodic refresh (every 60 seconds)
    intervalRef.current = setInterval(() => {
      fetchUnreadCounts();
    }, 60000);

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
        refetchTimeoutRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, fetchUnreadCounts, initSocket, handleVisibilityChange, dispatch]);

  // Expose a manual refresh function
  const refreshCounts = useCallback(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  return {
    totalCount,
    threads,
    roleBased,
    refreshCounts,
  };
};

export default useGlobalUnreadCount;
